import { useState } from "react";
import { Trash2, Download, Film, Music, Search, History as HistoryIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { runDownload } from "@/lib/download";
import { useLang } from "@/lib/i18n";
import { formatSize, formatDate } from "@/lib/utils-vg";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";

export const HistoryPanel = ({ open, onClose, items, onChange }) => {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState({}); // id -> percent

  const filtered = items.filter((it) => (it.title || "").toLowerCase().includes(query.toLowerCase()));

  const del = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      toast.success(t("toast.removed"));
      onChange();
    } catch {
      toast.error(t("toast.deleteFail"));
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/history");
      toast.success(t("toast.cleared"));
      onChange();
    } catch {
      toast.error(t("toast.clearFail"));
    }
  };

  const redownload = async (it) => {
    if (busy[it.id] != null) return;
    setBusy((b) => ({ ...b, [it.id]: 0 }));
    toast.info(t("toast.redownloading"));
    try {
      await runDownload({
        url: it.url,
        quality: it.quality || "best",
        onProgress: (p) => setBusy((b) => ({ ...b, [it.id]: Math.round(p.percent || 0) })),
      });
      toast.success(t("toast.downloadComplete"));
      onChange();
    } catch (e) {
      toast.error(e.message || t("toast.downloadFail"));
    } finally {
      setBusy((b) => {
        const n = { ...b };
        delete n[it.id];
        return n;
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="bg-[#0C0E15] border-white/10 text-slate-200 w-[92vw] max-w-md p-0 flex flex-col"
        data-testid="history-panel"
      >
        <SheetHeader className="p-5 border-b border-white/10">
          <SheetTitle className="text-white flex items-center gap-2 font-head">
            <HistoryIcon size={18} className="text-violet-400" /> {t("hist.title")}
          </SheetTitle>
          <SheetDescription className="sr-only">Your past downloads. Re-download or delete entries.</SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b border-white/10 flex gap-2">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder={t("hist.search")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-black/30 border-white/10 h-10 text-white"
              data-testid="history-search-input"
            />
          </div>
          {items.length > 0 && (
            <button
              onClick={clearAll}
              className="px-3 h-10 rounded-lg text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 transition-colors whitespace-nowrap"
              data-testid="history-clear-all-button"
            >
              {t("hist.clearAll")}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filtered.length === 0 ? (
            <div className="text-center text-slate-500 mt-16">
              <HistoryIcon size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">{items.length === 0 ? t("hist.empty") : t("hist.noMatch")}</p>
            </div>
          ) : (
            filtered.map((it) => (
              <div
                key={it.id}
                className="flex gap-3 p-3 rounded-xl bg-[#12141F] border border-white/8 hover:border-white/15 transition-colors"
                data-testid="history-item"
              >
                <div className="relative h-14 w-20 shrink-0 rounded-lg overflow-hidden bg-black/40 border border-white/10">
                  {it.thumbnail ? (
                    <img src={it.thumbnail} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-slate-600">
                      {it.file_type === "audio" ? <Music size={16} /> : <Film size={16} />}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white line-clamp-1">{it.title}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {it.platform} · {it.quality === "audio" ? "MP3" : it.quality === "best" ? "Best" : `${it.quality}p`}
                    {it.file_size ? ` · ${formatSize(it.file_size)}` : ""}
                  </p>
                  <p className="text-[11px] text-slate-600">
                    {busy[it.id] != null ? `${t("stage.downloading")} ${busy[it.id]}%` : formatDate(it.created_at)}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={() => redownload(it)}
                    disabled={busy[it.id] != null}
                    className="h-7 w-7 grid place-items-center rounded-lg bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 transition-colors disabled:opacity-60"
                    data-testid="history-redownload-button"
                    title={t("hist.redownload")}
                  >
                    {busy[it.id] != null ? <Loader2 size={13} className="vg-spin" /> : <Download size={13} />}
                  </button>
                  <button
                    onClick={() => del(it.id)}
                    className="h-7 w-7 grid place-items-center rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors"
                    data-testid="history-delete-button"
                    title={t("hist.delete")}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
